/**
 * Shop-floor kiosk terminal — the operator-facing screen a fixed shop-floor
 * PC runs full-screen. Public, no Entra auth: gated by a per-terminal
 * capability token (useKioskAuth) sent as an X-Kiosk-Terminal-Token header.
 * See docs/features/shopfloor-barcode-scanning/{01_product_brief,
 * 02_enterprise_architecture_impact_assessment,
 * 03_module_spec_shopfloor_kiosk}.md.
 *
 * Layout: a persistent left identity panel (the terminal's own display
 * name/site, resolved from its token via GET /shopfloor-kiosk/terminal;
 * clock; and — once logged in — operator name/shift start) alongside a
 * right panel that swaps between login and "ready to scan" content, both
 * using the same simple centered treatment (icon + headline + one action).
 *
 * Known open item, deliberately assumed for this pass — see chat for the
 * full flag-up: badge/NFC taps are parsed client-side as
 * "{operator_id}:{credential}" (kioskBadge.ts), since no server-side badge
 * parser exists yet. The real encoding needs confirming with whoever
 * provisions badges — see 99_outstanding_setup.md.
 *
 * Despatch tracking + unified job view + off-plan operations
 * (docs/features/shopfloor-despatch-tracking/, main body + Addendum A):
 * a scan no longer shows a bare "started X" confirmation — every scan
 * (and job-lookup) renders the same `JobTimelinePanel`, the job's unified
 * done/current/outstanding production + despatch timeline with the
 * expected-next step pre-highlighted, and the operator taps to progress.
 * `operation_choice_pending` now fires for **any** not-done-operation
 * count (including one) — see `applyScanEvent`.
 */
import { type FormEvent, useEffect, useRef, useState } from "react";
import { faro } from "@grafana/faro-web-sdk";
import {
  ArrowLeft,
  ArrowRightLeft,
  CheckCircle2,
  Circle,
  CircleDot,
  Coffee,
  Fingerprint,
  KeyRound,
  Layers,
  Loader2,
  LogOut,
  Minus,
  PackageCheck,
  Pause,
  Play,
  Plus,
  ScanLine,
  Search,
  Settings,
  Square,
  TriangleAlert,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { despatchStepLabel } from "@shared-core/domain/kioskDespatchSteps";
import { useKioskAuth } from "@shared-core/domain/kioskAuth";
import { parseBadgeTap } from "@shared-core/domain/kioskBadge";
import { describeHeldByOther } from "@shared-core/domain/kioskJobHandover";
import { milestoneGroup } from "@shared-core/domain/kioskJobMilestones";
import { formatTimeOrDash } from "@shared-core/domain/formatters";
import { ApiError } from "@shared-core/data/api/client";
import {
  extractErrorDetailMessage,
  extractFieldErrors,
  extractRefusalDetail,
} from "@shared-core/data/validation";
import type {
  KioskAttendanceStatus,
  KioskBundleChoice,
  KioskDespatchStep,
  KioskJobOperation,
  KioskJobOperationsLookup,
  KioskJobOperationStatus,
  KioskOperatorSession,
  KioskScanEvent,
  KioskSessionStartedVia,
  KioskShopfloorActivity,
  KioskTerminalIdentity,
} from "@shared-core/data/api-types";
import { Button } from "../app/components/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { currentTime } from "@shared-core/domain/current-time";

/** Ephemeral *success* feedback (accepted/despatch_recorded/took_over/
 * job_finished/despatch_already_complete) auto-reverts to "ready" after this
 * long — a brief confirmation the operator already expects. A refusal
 * (`rejected`/`partial_failure`) does NOT use this timer — see
 * `feedbackPersistsUntilDismissed` below. A kiosk is read standing up, at a
 * glance, often unexpectedly; for an unconfigured machine the refusal text
 * is the only thing telling the operator what to do instead, and 2.8s isn't
 * enough to read a full sentence you weren't expecting (docs/features/
 * shopfloor-barcode-scanning/98_known_issues.md). */
const FEEDBACK_DISMISS_MS = 2800;

/** The refusal family — shares its rendering in `FeedbackBanner` (the
 * XCircle branch) and, as of the known-issues fix above, shares an
 * exemption from the auto-dismiss timer: these persist until the operator
 * explicitly dismisses them or the next scan replaces `feedback` outright.
 * Every other `ScanFeedback` kind is a brief success confirmation and keeps
 * auto-clearing on `FEEDBACK_DISMISS_MS` as before. */
function feedbackPersistsUntilDismissed(feedback: ScanFeedback): boolean {
  return feedback.kind === "rejected" || feedback.kind === "partial_failure";
}

/** PINs are exactly 4 digits by decision (docs/features/
 * shopfloor-kiosk-operator-login/ decision 4). Used only to gate submission
 * on a *complete* PIN, not to validate it — the server is the sole judge of
 * whether a complete PIN is the right one. */
const PIN_LENGTH = 4;

function isCompletePin(pin: string): boolean {
  return pin.length === PIN_LENGTH;
}

type ScanFeedback =
  | { kind: "accepted"; jobNo: string }
  // ACCEPTED with despatch_step set (despatch-tracking spec §9) — the
  // despatch analogue of "accepted", same brief-confirmation treatment.
  | { kind: "despatch_recorded"; step: KioskDespatchStep }
  // DESPATCH_ALREADY_COMPLETE — benign informational outcome (job's
  // despatch is already fully recorded), not an error (spec §11).
  | { kind: "despatch_already_complete" }
  // Job hand-over spec — a take-over's accepted outcome, distinct from a
  // fresh "accepted" start so the operator sees it read as "continued",
  // not "started".
  | { kind: "took_over"; jobNo: string }
  // Job hand-over spec — the finish screen's confirm outcome.
  | {
      kind: "job_finished";
      finished: boolean;
      quantityGood: number;
      quantityWaste: number;
    }
  | { kind: "rejected"; reason: string }
  | { kind: "partial_failure"; reason: string };

/**
 * `status` mirrors the real Tharstern-backed attendance state
 * (`OperatorSessionResponse.attendance_status`), refreshed on every
 * login/reload. `startedAt` is a client-only decoration, populated only
 * when *this* session successfully calls startKioskShift; a shift
 * already in progress from a previous login/terminal shows no start
 * time here. The backend now also returns a real shift start timestamp
 * (`OperatorSessionResponse.shift_started_at`, added alongside the
 * Tharstern shopfloor parser field-name fix) that isn't wired into this
 * client-only decoration yet — using it to cover the "already in
 * progress" case, and where it should sit alongside the badge-in time,
 * is an open UI decision, not made here.
 */
interface ShiftState {
  status: KioskAttendanceStatus;
  startedAt: string | null;
}

const NO_SHIFT: ShiftState = { status: "not_clocked_in", startedAt: null };

interface ActiveJob {
  /** The Tharstern working-operation id — an opaque id, never shown to the
   * operator directly (see `jobNo`, the human-facing job number). Used only
   * as the pause/resume/finish target and as a last-resort display
   * fallback when `jobNo` is somehow unavailable. */
  code: string;
  paused: boolean;
  /** The human job number — always populated (a fresh choose-operation/
   * take-over/bundle-choice resolves `job_view`; a login-reconstructed
   * already-open job's `session.active_job` carries its own `job_no`).
   * `code` is never a substitute for this — display `jobNo`, not `code`. */
  jobNo?: string;
  /** Best-effort context for the finish screen — populated whenever the
   * operation was started this session (a fresh choose-operation, bundle
   * choice, or take-over resolves job_view, which carries this); left
   * undefined on a login-reconstructed already-open job, where only
   * `jobNo`/`code` are known. */
  activityName?: string;
  /** The real Tharstern machine this operation is booked against (site-
   * handhelds-and-machine-visibility update) — same "populated whenever
   * job_view was resolved, undefined on a login-reconstructed job"
   * caveat as activityName above. `undefined` also whenever Tharstern's
   * machine catalogue couldn't resolve this operation's resource —
   * render nothing in that case, never a placeholder. */
  machineName?: string;
  /** Seeds the finish screen's good-quantity field (job-handover update
   * spec §10 advisory) — undefined on a login-reconstructed job, same as
   * activityName above. */
  plannedQuantity?: number;
  /** Whether finishing this operation requires a nonblank comment
   * (kiosk-require-operator-comment fix) — same "populated whenever
   * job_view was resolved, undefined on a login-reconstructed job"
   * caveat as activityName above. Treated as `false` when undefined
   * (FinishJobScreen below): the real enforcement is end_job's own
   * validation, not this client-side hint, so an unresolved flag fails
   * open on the display rather than blocking a finish the server would
   * actually have allowed. */
  commentRequired?: boolean;
}

/** The "Look up a job" journey — a separate, opt-in mode from the golden-
 * path scan. Entered explicitly (button or looking up a job number); never
 * a side effect of a normal job-bag scan. See chat for the IA decision.
 * `note` is a one-off confirmation line (e.g. after an off-plan start
 * returns here per Addendum A2 — "starting ... returns to the job view"). */
type JobLookupStatus =
  | { kind: "entering" }
  | { kind: "loading" }
  | { kind: "not_found"; jobNo: string }
  | { kind: "error"; jobNo: string }
  | { kind: "loaded"; data: KioskJobOperationsLookup; note?: string };

/** The off-plan activity picker (Addendum A2) — available from both the
 * scan-driven job_view phase and the job_lookup phase, so it stores the
 * `returnPhase` to land back on ("returns to the job view" either way). */
type OffPlanStatus =
  | { kind: "loading" }
  | { kind: "ready"; activities: KioskShopfloorActivity[] }
  | { kind: "error" }
  | { kind: "starting"; activity: KioskShopfloorActivity }
  | {
      kind: "start_error";
      activities: KioskShopfloorActivity[];
      message: string;
    };

type Phase =
  | {
      kind: "login";
      mode: "start" | "pin";
      error: string | null;
      pending: boolean;
    }
  | {
      kind: "session";
      session: KioskOperatorSession;
      feedback: ScanFeedback | null;
    }
  | {
      kind: "conflict";
      session: KioskOperatorSession;
      scanEvent: KioskScanEvent;
    }
  // The unified job view (Addendum A1) — a scan resolved to
  // operation_choice_pending or despatch_choice_pending, waiting for an
  // explicit choose_job_operation / chooseKioskDespatchStep tap.
  | {
      kind: "job_view";
      session: KioskOperatorSession;
      scanEvent: KioskScanEvent;
    }
  // fix/joboperations-field-mismatch — the operator tapped a new
  // operation/bundle while one was still open (`scanEvent.previous_
  // working_operation_id` set). The terminal must ask for the good/waste
  // quantity on the operation being finished before finish-then-start
  // runs, using the same End-job-style input (see FinishJobScreen) —
  // never a Tharstern-sourced default. Only reached when there is
  // something to finish; nothing to finish skips straight to the choice.
  | {
      kind: "choice_quantity_prompt";
      session: KioskOperatorSession;
      scanEvent: KioskScanEvent;
      pendingChoice:
        | { kind: "operation"; operationId: number }
        | { kind: "bundle"; idcResourceSequenceId: number };
    }
  | {
      kind: "job_lookup";
      session: KioskOperatorSession;
      lookup: JobLookupStatus;
    }
  | {
      kind: "off_plan";
      session: KioskOperatorSession;
      jobNo: string;
      returnPhase: Phase;
      status: OffPlanStatus;
    }
  // Job hand-over spec — reached by tapping "End job"/"Finish" from the
  // Active/Paused screen; replaces the old silent finish-at-planned-
  // quantity behaviour with an explicit good/waste/finished decision.
  | {
      kind: "finish_job";
      session: KioskOperatorSession;
      activeJob: ActiveJob;
    }
  // Job hand-over spec — the "brief confirm" between tapping "Take over"
  // on a held-by-other row and it becoming the operator's own active job.
  | {
      kind: "take_over_confirm";
      session: KioskOperatorSession;
      jobNo: string;
      operation: KioskJobOperation;
      returnPhase: Phase;
    };

export function ShopfloorKioskPage() {
  const adapter = useDataAdapter();
  const { terminalToken, setTerminalToken, clearTerminalToken } =
    useKioskAuth();

  const [identity, setIdentity] = useState<KioskTerminalIdentity | null>(null);
  const [identityStatus, setIdentityStatus] = useState<
    "loading" | "ready" | "unauthorised" | "error"
  >("loading");

  const fetchIdentity = () => {
    setIdentityStatus("loading");
    adapter.getKioskTerminalIdentity().then(
      (result) => {
        setIdentity(result);
        setIdentityStatus("ready");
      },
      (err) => {
        // Only a genuine 401 (missing/unknown/deactivated token — spec §10's
        // identical-body invariant) means the token itself is bad. Anything
        // else (network blip, 5xx) is transient and must not tell the
        // operator/IT to re-provision a terminal that's actually fine.
        if (err instanceof ApiError && err.status === 401) {
          setIdentityStatus("unauthorised");
        } else {
          faro.api?.pushError(
            err instanceof Error
              ? err
              : new Error("shopfloor_kiosk.identity.fetch_failed"),
          );
          setIdentityStatus("error");
        }
      },
    );
  };

  useEffect(() => {
    if (!terminalToken) return;
    fetchIdentity();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchIdentity is stable per render intent; re-running on token change is what matters.
  }, [adapter, terminalToken]);

  const [phase, setPhase] = useState<Phase>({
    kind: "login",
    mode: "start",
    error: null,
    pending: false,
  });
  // Seeded fresh from the session's attendance_status on every login (see
  // attemptLogin) — real Tharstern-backed state, not a client-side guess.
  // A shift already in progress on a different terminal, or from before
  // this badge-in, shows up here correctly without any special-casing.
  const [currentShift, setCurrentShift] = useState<ShiftState>(NO_SHIFT);
  // Seeded fresh from session.active_job on every login too (see
  // attemptLogin) — a genuinely still-open Tharstern operation, not a
  // client-side guess; null in the common case (nothing open). No more
  // "unknown" third state (fail-hard decision,
  // docs/features/shopfloor-kiosk-fail-hard-when-tharstern-down/) — a
  // Tharstern outage now fails the login itself, so a successful login's
  // `activeJob === null` always means "asked Tharstern, genuinely
  // nothing open".
  const [activeJob, setActiveJob] = useState<ActiveJob | null>(null);
  // Off-plan activity search now varies on two independent inputs (query
  // AND department), so a slow response to an earlier search can land after
  // a faster response to a later one — a sequence guard on
  // searchOffPlanActivities makes only the most recently *issued* search
  // allowed to update `status`, regardless of resolution order.
  const offPlanSearchSeq = useRef(0);

  if (!terminalToken) {
    return <TerminalSetupScreen onSetUp={setTerminalToken} />;
  }

  if (identityStatus === "loading") {
    return (
      <FullBleed>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </FullBleed>
    );
  }

  if (identityStatus === "unauthorised") {
    return (
      <FullBleed>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
          <TriangleAlert className="size-12 text-amber-500" />
          <h1 className="text-xl font-semibold">
            This terminal isn't recognised
          </h1>
          <p className="text-muted-foreground max-w-sm">
            Its saved token is missing, unknown, or was deactivated. Re-run
            terminal setup with a fresh token.
          </p>
          <Button variant="outline" onClick={clearTerminalToken}>
            Redo terminal setup
          </Button>
        </div>
      </FullBleed>
    );
  }

  if (identityStatus === "error") {
    return (
      <FullBleed>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
          <TriangleAlert className="size-12 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Couldn't reach the server</h1>
          <p className="text-muted-foreground max-w-sm">
            This terminal's token looks fine — check the network connection and
            try again.
          </p>
          <Button variant="outline" onClick={fetchIdentity}>
            Try again
          </Button>
        </div>
      </FullBleed>
    );
  }

  async function handleBadgeTap(rawPayload: string) {
    const parsed = parseBadgeTap(rawPayload);
    if (!parsed) {
      setPhase({
        kind: "login",
        mode: "start",
        error: "Unrecognised badge — try again or use PIN.",
        pending: false,
      });
      return;
    }
    await attemptLogin(parsed.operatorId, parsed.credential, "badge_barcode");
  }

  async function attemptLogin(
    operatorId: number,
    credential: string,
    credentialType: KioskSessionStartedVia,
  ) {
    setPhase({ kind: "login", mode: "pin", error: null, pending: true });
    try {
      const session = await adapter.startKioskSession({
        operator_id: operatorId,
        credential,
        credential_type: credentialType,
      });
      // Fresh from the server on every login — real Tharstern attendance
      // state, not a client-side guess. No known start time for a shift
      // that was already running before this badge-in.
      setCurrentShift({ status: session.attendance_status, startedAt: null });
      // Same seam: session.active_job reconstructs a genuinely still-open
      // job (update spec §4/§5) — null in the common case (nothing open),
      // exactly as before. `code` is the working-operation id (the pause/
      // resume/finish target); `job_no` is carried on the response too, so
      // the reconstructed screen shows the same human job number a fresh
      // choose-operation/bundle accept would (activityName/plannedQuantity
      // stay unknown here — the response carries no operation detail).
      setActiveJob(
        session.active_job
          ? {
              code: String(session.active_job.operation_id),
              jobNo: session.active_job.job_no,
              paused: session.active_job.paused,
            }
          : null,
      );
      setPhase({ kind: "session", session, feedback: null });
    } catch (err) {
      setPhase({
        kind: "login",
        mode: "pin",
        error: describeLoginError(err),
        pending: false,
      });
    }
  }

  async function handleLogout() {
    await adapter.endKioskSession();
    setPhase({ kind: "login", mode: "start", error: null, pending: false });
  }

  /** Surfaces a shift/break/job-control rejection using the same
   * session-feedback banner handleScan's rejection path renders — see
   * FeedbackBanner. Drops back to the plain "session" phase so the
   * banner is visible regardless of which phase (conflict/job_lookup)
   * the operator triggered the action from. */
  function rejectWithFeedback(session: KioskOperatorSession, reason: string) {
    setPhase({
      kind: "session",
      session,
      feedback: { kind: "rejected", reason },
    });
  }

  // Shift/break controls (Gap A) — the IdentityPanel buttons are visible
  // whenever there's a session, regardless of phase, so each handler
  // resolves its own session from the current phase rather than being
  // passed one.
  async function handleStartShift() {
    if (phase.kind === "login") return;
    const { session } = phase;
    try {
      const outcome = await adapter.startKioskShift();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the shift start.",
        );
        return;
      }
      setCurrentShift({
        status: "on_shift",
        startedAt: currentTime().toISOString(),
      });
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.shift.start_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  async function handleEndShift() {
    if (phase.kind === "login") return;
    const { session } = phase;
    try {
      const outcome = await adapter.endKioskShift();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the shift end.",
        );
        return;
      }
      setCurrentShift(NO_SHIFT);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.shift.end_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  async function handleStartBreak() {
    if (phase.kind === "login") return;
    const { session } = phase;
    try {
      const outcome = await adapter.startKioskBreak();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the break start.",
        );
        return;
      }
      setCurrentShift((shift) => ({ ...shift, status: "on_break" }));
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.break.start_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  async function handleEndBreak() {
    if (phase.kind === "login") return;
    const { session } = phase;
    try {
      const outcome = await adapter.endKioskBreak();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the break end.",
        );
        return;
      }
      setCurrentShift((shift) => ({ ...shift, status: "on_shift" }));
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.break.end_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  // Job pause/resume/end controls (Gap B) — only reachable from the
  // "session" phase (ReadyPanel), so callers pass the already-narrowed
  // session directly, mirroring handleScan/handleResolveConflict below.
  async function handlePauseJob(session: KioskOperatorSession) {
    try {
      const outcome = await adapter.pauseKioskJob();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the pause.",
        );
        return;
      }
      setActiveJob((job) => (job ? { ...job, paused: true } : job));
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.job.pause_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  async function handleResumeJob(session: KioskOperatorSession) {
    try {
      const outcome = await adapter.resumeKioskJob();
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected the resume.",
        );
        return;
      }
      setActiveJob((job) => (job ? { ...job, paused: false } : job));
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.job.resume_failed"),
      );
      rejectWithFeedback(
        session,
        extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  // Job hand-over spec, Journey 1 — "End job"/"Finish" no longer finishes
  // silently at the planned quantity; it opens the finish screen for an
  // explicit good/waste/finished decision (see FinishJobScreen below).
  function handleOpenFinishJob(
    session: KioskOperatorSession,
    activeJobToFinish: ActiveJob | null,
  ) {
    if (!activeJobToFinish) return; // defensive — the button only renders when activeJob is set.
    setPhase({ kind: "finish_job", session, activeJob: activeJobToFinish });
  }

  function handleCancelFinishJob(session: KioskOperatorSession) {
    setPhase({ kind: "session", session, feedback: null });
  }

  async function handleConfirmFinish(
    session: KioskOperatorSession,
    finished: boolean,
    quantityGood: number,
    quantityWaste: number,
    comment: string,
  ) {
    try {
      const outcome = await adapter.endKioskJob({
        quantityGood,
        quantityWaste,
        finished,
        comment: comment || undefined,
      });
      if (!outcome.success) {
        rejectWithFeedback(
          session,
          outcome.problems.join("; ") || "Tharstern rejected ending the job.",
        );
        return;
      }
      setActiveJob(null);
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "job_finished",
          finished,
          quantityGood,
          quantityWaste,
        },
      });
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.job.end_failed"),
      );
      // Second line of defence, kiosk-require-operator-comment fix — the
      // client already blocks an empty required comment (commentMissing in
      // FinishJobScreen), but if that hint was stale/unresolved, end_job's
      // own validation rejects here with a field error whose own message
      // ("A comment is required...") is far more actionable in this
      // banner-only page than the generic "Input validation failed with N
      // error(s)" envelope message extractErrorDetailMessage would show.
      const fieldErrors = extractFieldErrors(err);
      rejectWithFeedback(
        session,
        fieldErrors[0]?.message ??
          extractErrorDetailMessage(err) ??
          "Couldn't reach the server — try again.",
      );
    }
  }

  async function handleScan(
    scannedCode: string,
    session: KioskOperatorSession,
  ) {
    try {
      const scanEvent = await adapter.submitKioskScan({
        scanned_code: scannedCode,
      });
      applyScanEvent(scanEvent, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.scan.submit_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: "Couldn't reach the server — try again.",
        },
      });
    }
  }

  /** Populates ActiveJob's finish-screen context (jobNo/activityName/
   * machineName/plannedQuantity) from an accepted scan event's job_view,
   * alongside the pre-existing `code` (the working-operation id) — shared
   * by a normal choose-operation accept and a take-over accept (job
   * hand-over spec) so both land on the same fully-populated finish
   * screen later.
   *
   * The milestone lookup joins on `new_job_operation_id`, never
   * `new_working_operation_id` — the two are different Tharstern id
   * spaces that overlap in range, so joining the WorkingOperation id
   * straight into job_view's JobOperation-keyed milestones can silently
   * resolve to the wrong row (see ScanEventResponse.new_job_operation_id's
   * docstring). Returns the resolved human job number for the caller's
   * feedback banner — never the opaque working-operation id. */
  function applyAcceptedJobStart(scanEvent: KioskScanEvent): string {
    const workingOperationId = scanEvent.new_working_operation_id ?? "—";
    const jobView = scanEvent.job_view;
    const milestone: KioskJobOperation | undefined = jobView?.milestones.find(
      (m) => m.operation_id === scanEvent.new_job_operation_id,
    );
    setActiveJob({
      code: workingOperationId,
      paused: false,
      jobNo: jobView?.job_no,
      activityName: milestone?.activity_name,
      machineName: milestone?.machine_name ?? undefined,
      plannedQuantity: milestone?.planned_quantity ?? undefined,
      commentRequired: milestone?.comment_required ?? undefined,
    });
    return jobView?.job_no ?? "—";
  }

  /** Addendum A1 — a scan never auto-starts or auto-records anymore. Every
   * outcome except accepted/rejected/partial-failure/conflict lands on the
   * unified job_view phase (the done/current/outstanding timeline, expected
   * next pre-highlighted) and waits for an explicit tap. */
  function applyScanEvent(
    scanEvent: KioskScanEvent,
    session: KioskOperatorSession,
  ) {
    if (scanEvent.outcome === "conflict_pending") {
      setPhase({ kind: "conflict", session, scanEvent });
      return;
    }
    if (
      scanEvent.outcome === "operation_choice_pending" ||
      scanEvent.outcome === "despatch_choice_pending"
    ) {
      setPhase({ kind: "job_view", session, scanEvent });
      return;
    }
    if (scanEvent.outcome === "accepted") {
      // despatch_step set -> a despatch-step tap; new_working_operation_id
      // set -> a production choose_job_operation tap. Both are brief
      // confirmations back on the ready-to-scan screen (spec §9/Addendum A1).
      if (scanEvent.despatch_step) {
        setPhase({
          kind: "session",
          session,
          feedback: {
            kind: "despatch_recorded",
            step: scanEvent.despatch_step,
          },
        });
        return;
      }
      const jobNo = applyAcceptedJobStart(scanEvent);
      setPhase({
        kind: "session",
        session,
        feedback: { kind: "accepted", jobNo },
      });
      return;
    }
    if (scanEvent.outcome === "despatch_already_complete") {
      setPhase({
        kind: "session",
        session,
        feedback: { kind: "despatch_already_complete" },
      });
      return;
    }
    if (scanEvent.outcome === "partial_failure") {
      // The previous job finished but the new one never started — nothing
      // is active now.
      setActiveJob(null);
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "partial_failure",
          reason:
            "Finished your last Tharstern job but couldn't start the new one — a ticket's been raised.",
        },
      });
      return;
    }
    setPhase({
      kind: "session",
      session,
      feedback: {
        kind: "rejected",
        reason:
          scanEvent.rejection_reason ?? "Barcode not recognised — try again.",
      },
    });
  }

  function handleOpenJobLookup(session: KioskOperatorSession) {
    setPhase({ kind: "job_lookup", session, lookup: { kind: "entering" } });
  }

  async function handleLookupJob(session: KioskOperatorSession, jobNo: string) {
    setPhase({ kind: "job_lookup", session, lookup: { kind: "loading" } });
    try {
      const data = await adapter.lookupKioskJobOperations(jobNo);
      // No 404 on this route — an unknown job number comes back with empty
      // milestones AND no despatch history either (the only case despatch's
      // universal "ready for despatch once production is done" reading
      // can't distinguish from a real, fully-despatched job — see the
      // despatch-tracking spec §11's REJECTED_NO_OPERATIONS_REMAINING note).
      const looksUnknown =
        data.milestones.length === 0 &&
        (data.despatch_steps_done?.length ?? 0) === 0;
      setPhase({
        kind: "job_lookup",
        session,
        lookup: looksUnknown
          ? { kind: "not_found", jobNo }
          : { kind: "loaded", data },
      });
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.job_lookup.failed"),
      );
      setPhase({
        kind: "job_lookup",
        session,
        lookup: { kind: "error", jobNo },
      });
    }
  }

  function handleBackToScanning(session: KioskOperatorSession) {
    setPhase({ kind: "session", session, feedback: null });
  }

  async function handleResolveConflict(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    confirm: boolean,
  ) {
    try {
      const resolved = await adapter.resolveKioskConflict(scanEvent.id, {
        confirm,
      });
      applyScanEvent(resolved, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.conflict.resolve_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: describeServerRefusal(
            err,
            "That didn't go through — scan the Tharstern job again.",
          ),
        },
      });
    }
  }

  async function handleChooseOperation(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    operationId: number,
    quantityGood = 0,
    quantityWaste = 0,
    comment?: string,
  ) {
    try {
      const resolved = await adapter.chooseKioskJobOperation(
        scanEvent.id,
        operationId,
        quantityGood,
        quantityWaste,
        comment,
      );
      applyScanEvent(resolved, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.operation_choice.resolve_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: describeOperationChoiceRefusal(err, scanEvent, {
            kind: "operation",
            operationId,
          }),
        },
      });
    }
  }

  /** The bundle sibling of handleChooseOperation
   * (docs/features/shopfloor-operation-bundles/ update spec §9) — same
   * re-resolve-fresh-server-side shape, keyed by the bundle's
   * idc_resource_sequence_id rather than an operation id. */
  async function handleChooseBundle(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    idcResourceSequenceId: number,
    quantityGood = 0,
    quantityWaste = 0,
    comment?: string,
  ) {
    try {
      const resolved = await adapter.chooseKioskOperationBundle(
        scanEvent.id,
        idcResourceSequenceId,
        quantityGood,
        quantityWaste,
        comment,
      );
      applyScanEvent(resolved, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.bundle_choice.resolve_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: describeOperationChoiceRefusal(err, scanEvent, {
            kind: "bundle",
            idcResourceSequenceId,
          }),
        },
      });
    }
  }

  /** Gate for both operation and bundle taps in the job_view picker
   * (fix/joboperations-field-mismatch): a previous operation open at this
   * terminal (`previous_working_operation_id` set) must be finished with
   * the operator's real quantity before finish-then-start runs, so this
   * detours to the quantity prompt instead of choosing immediately.
   * Nothing open — the common single-candidate start — skips straight to
   * the choice, same as before this change. */
  function handleOperationOrBundleChoiceTapped(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    pendingChoice:
      | { kind: "operation"; operationId: number }
      | { kind: "bundle"; idcResourceSequenceId: number },
  ) {
    if (scanEvent.previous_working_operation_id) {
      setPhase({
        kind: "choice_quantity_prompt",
        session,
        scanEvent,
        pendingChoice,
      });
      return;
    }
    if (pendingChoice.kind === "operation") {
      handleChooseOperation(session, scanEvent, pendingChoice.operationId);
    } else {
      handleChooseBundle(
        session,
        scanEvent,
        pendingChoice.idcResourceSequenceId,
      );
    }
  }

  function handleConfirmChoiceQuantity(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    pendingChoice:
      | { kind: "operation"; operationId: number }
      | { kind: "bundle"; idcResourceSequenceId: number },
    quantityGood: number,
    quantityWaste: number,
    comment: string,
  ) {
    if (pendingChoice.kind === "operation") {
      handleChooseOperation(
        session,
        scanEvent,
        pendingChoice.operationId,
        quantityGood,
        quantityWaste,
        comment || undefined,
      );
    } else {
      handleChooseBundle(
        session,
        scanEvent,
        pendingChoice.idcResourceSequenceId,
        quantityGood,
        quantityWaste,
        comment || undefined,
      );
    }
  }

  async function handleChooseDespatchStep(
    session: KioskOperatorSession,
    scanEvent: KioskScanEvent,
    step: KioskDespatchStep,
  ) {
    try {
      const resolved = await adapter.chooseKioskDespatchStep(
        scanEvent.id,
        step,
      );
      applyScanEvent(resolved, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.despatch_choice.resolve_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: describeServerRefusal(
            err,
            `${despatchStepLabel(step)} just changed — scan the Tharstern job again to see what's outstanding.`,
          ),
        },
      });
    }
  }

  // ------------------------------------------- Take-over (job hand-over) ---
  // Journey 2/3 — continuing an operation in progress / left incomplete by
  // a *different* operator. The confirm step calls the dedicated take-over
  // endpoint directly (spec §5/§9/§11's server-side-resolution invariant —
  // the holder and the operation's state are re-checked fresh server-side,
  // never trusted from the client's picker state); a 409 (job moved on
  // since the row was shown) surfaces as a rejection like any other stale
  // tap.

  function handleOpenTakeOverConfirm(
    session: KioskOperatorSession,
    jobNo: string,
    operation: KioskJobOperation,
    returnPhase: Phase,
  ) {
    setPhase({
      kind: "take_over_confirm",
      session,
      jobNo,
      operation,
      returnPhase,
    });
  }

  async function handleConfirmTakeOver(
    session: KioskOperatorSession,
    jobNo: string,
    operation: KioskJobOperation,
    comment: string,
  ) {
    try {
      const resolved = await adapter.takeOverKioskOperation({
        jobNo,
        operationId: operation.operation_id,
        comment: comment || undefined,
      });
      if (
        resolved.outcome === "accepted" &&
        resolved.new_working_operation_id
      ) {
        const jobNo = applyAcceptedJobStart(resolved);
        setPhase({
          kind: "session",
          session,
          feedback: { kind: "took_over", jobNo },
        });
        return;
      }
      applyScanEvent(resolved, session);
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.job.take_over_failed"),
      );
      setPhase({
        kind: "session",
        session,
        feedback: {
          kind: "rejected",
          reason: describeServerRefusal(
            err,
            "Couldn't take over that Tharstern job — try again.",
          ),
        },
      });
    }
  }

  // ----------------------------------------------- Off-plan (Addendum A2) ---
  // Available from both the job_view (scan) and job_lookup phases, from the
  // same job view — a picker over GET /activities (server-side filter/
  // search; never filtered client-side) that starts an operation not on the
  // job's Tharstern route.

  function handleOpenOffPlan(
    session: KioskOperatorSession,
    jobNo: string,
    returnPhase: Phase,
  ) {
    setPhase({
      kind: "off_plan",
      session,
      jobNo,
      returnPhase,
      status: { kind: "loading" },
    });
    void searchOffPlanActivities(
      session,
      jobNo,
      returnPhase,
      undefined,
      undefined,
    );
  }

  async function searchOffPlanActivities(
    session: KioskOperatorSession,
    jobNo: string,
    returnPhase: Phase,
    query: string | undefined,
    departmentId: number | undefined,
  ) {
    const seq = ++offPlanSearchSeq.current;
    try {
      const activities = await adapter.listKioskActivities({
        q: query,
        departmentId,
      });
      if (seq !== offPlanSearchSeq.current) return; // superseded by a later search
      setPhase({
        kind: "off_plan",
        session,
        jobNo,
        returnPhase,
        status: { kind: "ready", activities },
      });
    } catch (err) {
      if (seq !== offPlanSearchSeq.current) return; // superseded by a later search
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.off_plan.activities_failed"),
      );
      setPhase({
        kind: "off_plan",
        session,
        jobNo,
        returnPhase,
        status: { kind: "error" },
      });
    }
  }

  async function handleStartOffPlan(
    session: KioskOperatorSession,
    jobNo: string,
    returnPhase: Phase,
    activity: KioskShopfloorActivity,
    currentActivities: KioskShopfloorActivity[],
  ) {
    setPhase({
      kind: "off_plan",
      session,
      jobNo,
      returnPhase,
      status: { kind: "starting", activity },
    });
    try {
      const jobView = await adapter.startKioskOffPlanOperation(
        jobNo,
        activity.id,
      );
      // "Starting records the operation and returns to the job view"
      // (Addendum A2) — the read-only job_lookup rendering IS that view,
      // regardless of whether off-plan was opened from a scan or a lookup.
      setPhase({
        kind: "job_lookup",
        session,
        lookup: {
          kind: "loaded",
          data: jobView,
          note: `Started off-plan: ${activity.name}`,
        },
      });
    } catch (err) {
      faro.api?.pushError(
        err instanceof Error
          ? err
          : new Error("shopfloor_kiosk.off_plan.start_failed"),
      );
      setPhase({
        kind: "off_plan",
        session,
        jobNo,
        returnPhase,
        status: {
          kind: "start_error",
          activities: currentActivities,
          message: describeServerRefusal(
            err,
            "Couldn't start that operation — try again.",
            "Couldn't reach the server — try again.",
          ),
        },
      });
    }
  }

  return (
    <FullBleed>
      <IdentityPanel
        identity={identity}
        session={phase.kind !== "login" ? phase.session : null}
        shift={currentShift}
        onStartShift={handleStartShift}
        onStartBreak={handleStartBreak}
        onEndBreak={handleEndBreak}
        onEndShift={handleEndShift}
        onLogout={phase.kind !== "login" ? handleLogout : undefined}
        onResetTerminal={clearTerminalToken}
      />
      {/* Own scroll container (docs/features/shopfloor-barcode-scanning/
       * 98_known_issues.md, issue 3): FullBleed's root is `h-screen
       * overflow-hidden`, so this flex-1 column takes a fixed, stretched
       * height and never gets a scrollbar of its own unless it declares
       * one. It used to be a single `flex items-center justify-center`
       * box with no overflow handling — fine for LoginPanel/ReadyPanel's
       * short, centered content, but a tall JobTimelinePanel (many
       * operations) simply overflowed that box's edges with nothing to
       * clip or scroll it *except* the ancestor's overflow-hidden, which
       * clipped it symmetrically top and bottom around the centered
       * midpoint — cutting off the first (and most important, "Expected
       * next") row along with the last. Splitting scroll (outer,
       * overflow-y-auto) from centering (inner, min-h-full) keeps short
       * content centered as before while tall content starts cleanly at
       * the top and scrolls within its own bounded viewport instead of
       * the page. */}
      <div className="flex-1 min-w-0 overflow-y-auto relative">
        <div className="min-h-full flex items-center justify-center p-8">
          {phase.kind === "login" && (
            <LoginPanel
              phase={phase}
              onBadgeTap={handleBadgeTap}
              onPinSubmit={(operatorId, pin) =>
                attemptLogin(operatorId, pin, "pin")
              }
              onSwitchMode={(mode) => setPhase({ ...phase, mode, error: null })}
            />
          )}
          {phase.kind === "session" && (
            <ReadyPanel
              feedback={phase.feedback}
              activeJob={activeJob}
              onScan={(code) => handleScan(code, phase.session)}
              onFeedbackDismissed={() => setPhase({ ...phase, feedback: null })}
              onPauseJob={() => handlePauseJob(phase.session)}
              onResumeJob={() => handleResumeJob(phase.session)}
              onEndJob={() => handleOpenFinishJob(phase.session, activeJob)}
              onLookUpJob={() => handleOpenJobLookup(phase.session)}
            />
          )}
          {phase.kind === "conflict" && (
            <ConflictTakeover
              scanEvent={phase.scanEvent}
              onResolve={(confirm) =>
                handleResolveConflict(phase.session, phase.scanEvent, confirm)
              }
            />
          )}
          {phase.kind === "job_view" && (
            <JobViewScanPanel
              scanEvent={phase.scanEvent}
              onChooseOperation={(operationId) =>
                handleOperationOrBundleChoiceTapped(
                  phase.session,
                  phase.scanEvent,
                  {
                    kind: "operation",
                    operationId,
                  },
                )
              }
              onChooseBundle={(idcResourceSequenceId) =>
                handleOperationOrBundleChoiceTapped(
                  phase.session,
                  phase.scanEvent,
                  {
                    kind: "bundle",
                    idcResourceSequenceId,
                  },
                )
              }
              onChooseDespatchStep={(step) =>
                handleChooseDespatchStep(phase.session, phase.scanEvent, step)
              }
              onOffPlan={(jobNo) =>
                handleOpenOffPlan(phase.session, jobNo, phase)
              }
              onTakeOver={(jobNo, operation) =>
                handleOpenTakeOverConfirm(
                  phase.session,
                  jobNo,
                  operation,
                  phase,
                )
              }
            />
          )}
          {phase.kind === "choice_quantity_prompt" && (
            <ChoiceQuantityPromptScreen
              scanEvent={phase.scanEvent}
              onConfirm={(quantityGood, quantityWaste, comment) =>
                handleConfirmChoiceQuantity(
                  phase.session,
                  phase.scanEvent,
                  phase.pendingChoice,
                  quantityGood,
                  quantityWaste,
                  comment,
                )
              }
              onCancel={() =>
                setPhase({
                  kind: "job_view",
                  session: phase.session,
                  scanEvent: phase.scanEvent,
                })
              }
            />
          )}
          {phase.kind === "job_lookup" && (
            <JobLookupPanel
              lookup={phase.lookup}
              onLookup={(jobNo) => handleLookupJob(phase.session, jobNo)}
              onStart={(code) => handleScan(code, phase.session)}
              onOffPlan={(jobNo) =>
                handleOpenOffPlan(phase.session, jobNo, phase)
              }
              onTakeOver={(jobNo, operation) =>
                handleOpenTakeOverConfirm(
                  phase.session,
                  jobNo,
                  operation,
                  phase,
                )
              }
              onBack={() => handleBackToScanning(phase.session)}
            />
          )}
          {phase.kind === "finish_job" && (
            <FinishJobScreen
              activeJob={phase.activeJob}
              onConfirm={(finished, quantityGood, quantityWaste, comment) =>
                handleConfirmFinish(
                  phase.session,
                  finished,
                  quantityGood,
                  quantityWaste,
                  comment,
                )
              }
              onCancel={() => handleCancelFinishJob(phase.session)}
            />
          )}
          {phase.kind === "take_over_confirm" && (
            <TakeOverConfirm
              operation={phase.operation}
              onConfirm={(comment) =>
                handleConfirmTakeOver(
                  phase.session,
                  phase.jobNo,
                  phase.operation,
                  comment,
                )
              }
              onCancel={() => setPhase(phase.returnPhase)}
            />
          )}
          {phase.kind === "off_plan" && (
            <OffPlanPicker
              status={phase.status}
              onSearch={(query, departmentId) =>
                searchOffPlanActivities(
                  phase.session,
                  phase.jobNo,
                  phase.returnPhase,
                  query,
                  departmentId,
                )
              }
              onStart={(activity, currentActivities) =>
                handleStartOffPlan(
                  phase.session,
                  phase.jobNo,
                  phase.returnPhase,
                  activity,
                  currentActivities,
                )
              }
              onCancel={() => setPhase(phase.returnPhase)}
            />
          )}
        </div>
      </div>
    </FullBleed>
  );
}

function describeLoginError(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (message.includes("locked out") || message.includes("too many")) {
    return "Too many failed PIN attempts — wait a moment and try again.";
  }
  // The credential itself is verified locally against our own
  // operator-credential store, so a rejection here is never Tharstern's
  // call to make — but login is not Tharstern-independent overall: fail-
  // hard decision (docs/features/shopfloor-kiosk-fail-hard-when-
  // tharstern-down/), a correct credential still has to clear an
  // attendance/active-job round trip to Tharstern before the terminal
  // logs the operator in, and that now fails the whole login (502) if
  // Tharstern is unreachable. This must read as a Tharstern/system fault
  // the operator can act on ("tell a supervisor"), never as "check the
  // PIN" — that copy sent a whole shift round a retry loop during the
  // 2026-08-17 outage, burning lockout attempts on a correct PIN while
  // the real (Tharstern) fault stayed invisible. Do NOT delete this
  // branch or collapse it into the generic 5xx one below.
  if (err instanceof ApiError && err.status === 502) {
    return "Can't reach Tharstern right now — tell a supervisor, don't keep retrying your PIN.";
  }
  // A non-502 5xx is a different, still-real outcome: our own credential
  // check can itself fail (database unavailable, an unhandled bug) with
  // Tharstern not at fault. Same "tell a supervisor, don't retry" advice,
  // but must not name Tharstern — naming the wrong system sends whoever
  // reads it looking in the wrong place.
  if (err instanceof ApiError && err.status >= 500) {
    return "This terminal can't check your PIN right now — tell a supervisor, don't keep retrying.";
  }
  return "Not recognised — check the PIN and try again.";
}

// ---------------------------------------------- Picker-refusal copy ---
// The job-view picker's handlers (handleChooseOperation/handleChooseBundle/
// handleChooseDespatchStep/handleResolveConflict/handleConfirmTakeOver/
// handleStartOffPlan) used to collapse every catch into "Couldn't reach
// the server — scan again to retry.", regardless of whether the server
// ever responded — the same mistake `describeLoginError` above exists to
// avoid for login (PR #1307), repeated here for the operation-choice 409
// a resource with no IDC activity configuration produces (chat,
// 2026-08-26): the server answered with a specific, permanent refusal,
// and the terminal told the operator it hadn't been reached at all, then
// invited a retry that can never succeed. These helpers are the shared
// fix for that whole family of catches, not just the one that was
// reported.

/** True only when the request reached the server and it responded — with
 * any status, success or refusal. `ApiError` is thrown exclusively by
 * `apiClient`'s `request()` after `fetch` resolves and the response body
 * is read (see `data/api/client.ts`); a genuine network/DNS/CORS failure
 * throws before any response exists and is never an `ApiError`. */
function serverAnswered(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Shared refusal copy for handlers with no richer on-screen context to
 * build a specific message from (resolve-conflict, take-over, off-plan
 * start). Reads the backend's own `{message}` when present and falls
 * back to `whenAnswered` — never to `whenUnreached`, since `serverAnswered`
 * is already true by the time that fallback is used. `whenUnreached`
 * defaults to the scan-flow's usual copy; off-plan start (reached from a
 * picker, not a scan) overrides it. */
function describeServerRefusal(
  err: unknown,
  whenAnswered: string,
  whenUnreached = "Couldn't reach the server — scan again to retry.",
): string {
  if (!serverAnswered(err)) {
    return whenUnreached;
  }
  // Second line of defence, kiosk-require-operator-comment fix — several
  // call sites (take-over, end-job) block a blank required comment
  // client-side already, but if that hint was stale/unresolved, the
  // action's own validation rejects with a field error whose own message
  // ("A comment is required...") is far more actionable in this
  // banner-only page than the generic "Input validation failed with N
  // error(s)" envelope message extractErrorDetailMessage would show.
  const fieldErrors = extractFieldErrors(err);
  return (
    fieldErrors[0]?.message ?? extractErrorDetailMessage(err) ?? whenAnswered
  );
}

type OperationOrBundleChoice =
  | { kind: "operation"; operationId: number }
  | { kind: "bundle"; idcResourceSequenceId: number };

/** The on-screen label for a tapped operation/bundle choice — the exact
 * words already rendered in the picker (`activity_name` / bundle `name`),
 * read back off the scan event rather than re-derived, so a refusal
 * message names the same thing the operator just tapped. */
function operationChoiceLabel(
  scanEvent: KioskScanEvent,
  pendingChoice: OperationOrBundleChoice,
): string {
  const choices = scanEvent.operation_choices ?? [];
  if (pendingChoice.kind === "operation") {
    const match = choices.find(
      (c) =>
        c.kind === "operation" && c.operation_id === pendingChoice.operationId,
    );
    return match?.kind === "operation" ? match.activity_name : "That operation";
  }
  const match = choices.find(
    (c) =>
      c.kind === "bundle" &&
      c.idc_resource_sequence_id === pendingChoice.idcResourceSequenceId,
  );
  return match?.kind === "bundle" ? match.name : "That operation";
}

/** handleChooseOperation/handleChooseBundle's shared refusal copy. A 409
 * carrying `reason_code: "no_activity_configured"` or
 * `"ambiguous_planned_activity"` (kiosk.py's `_operation_choice_refusal_
 * detail`) is a *permanent* refusal, not a transient one: no fault of
 * the operator's, and rescanning won't change the machine's Tharstern
 * configuration — so this names the operation in the words already on
 * screen, says so, and points at the real remedy (the off-plan picker /
 * the bundle option) instead of the generic "try again" every other
 * picker refusal gets below. */
function describeOperationChoiceRefusal(
  err: unknown,
  scanEvent: KioskScanEvent,
  pendingChoice: OperationOrBundleChoice,
): string {
  if (!serverAnswered(err)) {
    return "Couldn't reach the server — scan again to retry.";
  }
  const label = operationChoiceLabel(scanEvent, pendingChoice);
  const refusal = extractRefusalDetail(err);
  if (refusal?.reasonCode === "no_activity_configured") {
    return `${label} has no machine set up to start it automatically — that's not something rescanning fixes. Scan the Tharstern job again and use "Off-plan / different machine" instead.`;
  }
  if (refusal?.reasonCode === "ambiguous_planned_activity") {
    return `${label} has more than one machine set up for it — scan the Tharstern job again and choose the bundle option instead of a plain start.`;
  }
  // Second line of defence, kiosk-require-operator-comment fix — the
  // client already blocks an empty required comment in
  // ChoiceQuantityPromptScreen, but if that hint was stale/unresolved,
  // finish_then_start's own validation rejects here with a field error
  // whose own message is far more actionable than the generic "Input
  // validation failed with N error(s)" envelope message would be.
  const fieldErrors = extractFieldErrors(err);
  return (
    fieldErrors[0]?.message ??
    extractErrorDetailMessage(err) ??
    `${label} just changed — scan the Tharstern job again to see the current options.`
  );
}

// -------------------------------------------------------------- Shell ---

function FullBleed({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen w-full overflow-hidden flex bg-background text-foreground select-none">
      {children}
    </div>
  );
}

function useClock(): string {
  const [now, setNow] = useState(() => currentTime());
  useEffect(() => {
    const id = setInterval(() => setNow(currentTime()), 30_000);
    return () => clearInterval(id);
  }, []);
  return `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

// ---------------------------------------------------------- Identity ---

function IdentityPanel({
  identity,
  session,
  shift,
  onStartShift,
  onStartBreak,
  onEndBreak,
  onEndShift,
  onLogout,
  onResetTerminal,
}: {
  identity: KioskTerminalIdentity | null;
  session: KioskOperatorSession | null;
  shift: ShiftState;
  onStartShift: () => void;
  onStartBreak: () => void;
  onEndBreak: () => void;
  onEndShift: () => void;
  onLogout?: () => void;
  onResetTerminal: () => void;
}) {
  const time = useClock();
  return (
    <aside className="w-72 shrink-0 border-r border-border bg-card/40 flex flex-col p-6">
      <div className="text-lg font-semibold">
        {identity?.display_name ?? "This terminal"}
      </div>
      <div className="text-3xl font-semibold tabular-nums mt-1">{time}</div>

      {session && (
        <div className="mt-8 pt-6 border-t border-border space-y-1">
          <div className="text-sm text-muted-foreground">Badged in</div>
          <div className="text-xl font-medium">
            {session.operator_display_name}
          </div>
          <div className="text-sm text-muted-foreground">
            Since {formatTimeOrDash(session.started_at)}
          </div>
          {shift.startedAt && (
            <div className="text-sm text-muted-foreground">
              Shift started {formatTimeOrDash(shift.startedAt)}
            </div>
          )}
        </div>
      )}

      <div className="flex-1" />

      {session && (
        <div className="space-y-2 mb-3">
          {shift.status === "not_clocked_in" && (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={onStartShift}
            >
              <Play className="size-4" /> Start shift
            </Button>
          )}
          {shift.status === "on_shift" && (
            <>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={onStartBreak}
              >
                <Coffee className="size-4" /> Start break
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={onEndShift}
              >
                <Square className="size-4" /> End shift
              </Button>
            </>
          )}
          {shift.status === "on_break" && (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={onEndBreak}
            >
              <Coffee className="size-4" /> End break
            </Button>
          )}
        </div>
      )}

      {onLogout ? (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-2"
          onClick={onLogout}
        >
          <LogOut className="size-4" /> Log out
        </Button>
      ) : (
        <button
          onClick={onResetTerminal}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start"
        >
          <Settings className="size-3.5" /> Terminal settings
        </button>
      )}
    </aside>
  );
}

// --------------------------------------------------------- Login/PIN ---

function LoginPanel({
  phase,
  onBadgeTap,
  onPinSubmit,
  onSwitchMode,
}: {
  phase: Extract<Phase, { kind: "login" }>;
  onBadgeTap: (rawPayload: string) => void;
  onPinSubmit: (operatorId: number, pin: string) => void;
  onSwitchMode: (mode: "start" | "pin") => void;
}) {
  const badgeInputRef = useRef<HTMLInputElement>(null);
  const [badgeBuffer, setBadgeBuffer] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (phase.mode === "start") badgeInputRef.current?.focus();
  }, [phase.mode]);

  if (phase.mode === "pin") {
    function handleSubmit(e: FormEvent) {
      e.preventDefault();
      const id = Number(operatorId);
      // The PIN is now a real credential — every enrolled operator has one
      // (docs/features/shopfloor-kiosk-operator-login/ decision 4: exactly
      // 4 digits, admin-issued). There is no "operator has no PIN" case
      // left to wave through; an incomplete form just doesn't submit.
      // Gating on length alone (not just non-empty) matters here: a
      // 3-digit typo would otherwise submit, get rejected as a wrong
      // credential, and burn one of only 5 lockout attempts before the
      // operator has even finished typing.
      if (!Number.isInteger(id) || id <= 0) return;
      if (!isCompletePin(pin)) return;
      onPinSubmit(id, pin);
    }
    return (
      <div className="flex flex-col items-center gap-6 w-full max-w-xs">
        <KeyRound className="size-12 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">Enter your details</h1>
        <form onSubmit={handleSubmit} className="w-full space-y-3">
          <input
            type="number"
            inputMode="numeric"
            placeholder="Operator ID"
            value={operatorId}
            onChange={(e) => setOperatorId(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-4 py-3 text-lg text-center outline-none focus:border-primary"
            autoFocus
          />
          <input
            type="password"
            inputMode="numeric"
            maxLength={PIN_LENGTH}
            placeholder="PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-4 py-3 text-lg text-center tracking-[0.5em] outline-none focus:border-primary"
          />
          {phase.error && (
            <p className="text-sm text-center text-destructive">
              {phase.error}
            </p>
          )}
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={phase.pending || !isCompletePin(pin)}
          >
            {phase.pending ? "Checking…" : "Continue"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => onSwitchMode("start")}
          >
            Use badge instead
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <Fingerprint className="size-16 text-muted-foreground" />
      <h1 className="text-2xl font-semibold">Tap your badge to begin</h1>
      {phase.error && <p className="text-sm text-destructive">{phase.error}</p>}
      {/* Invisible focused input catches the scanner/NFC reader's HID
          keystrokes; scanners submit with a trailing Enter. */}
      <input
        ref={badgeInputRef}
        value={badgeBuffer}
        onChange={(e) => setBadgeBuffer(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && badgeBuffer.trim()) {
            onBadgeTap(badgeBuffer.trim());
            setBadgeBuffer("");
          }
        }}
        onBlur={() => badgeInputRef.current?.focus()}
        className="sr-only"
        aria-label="Badge scan input"
      />
      <Button variant="outline" size="lg" onClick={() => onSwitchMode("pin")}>
        Enter PIN instead
      </Button>
    </div>
  );
}

// ------------------------------------------------------- Ready/scan ---

function ReadyPanel({
  feedback,
  activeJob,
  onScan,
  onFeedbackDismissed,
  onPauseJob,
  onResumeJob,
  onEndJob,
  onLookUpJob,
}: {
  feedback: ScanFeedback | null;
  activeJob: ActiveJob | null;
  onScan: (scannedCode: string) => void;
  onFeedbackDismissed: () => void;
  onPauseJob: () => void;
  onResumeJob: () => void;
  onEndJob: () => void;
  onLookUpJob: () => void;
}) {
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [scanBuffer, setScanBuffer] = useState("");
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    if (!manualEntryOpen) scanInputRef.current?.focus();
  }, [manualEntryOpen]);

  useEffect(() => {
    if (!feedback) return;
    // A refusal stays up until the operator dismisses it (the banner's own
    // close button) or the next scan replaces `feedback` outright — see
    // feedbackPersistsUntilDismissed. Only success feedback auto-clears.
    if (feedbackPersistsUntilDismissed(feedback)) return;
    const id = setTimeout(onFeedbackDismissed, FEEDBACK_DISMISS_MS);
    return () => clearTimeout(id);
  }, [feedback, onFeedbackDismissed]);

  function submitManualCode(e: FormEvent) {
    e.preventDefault();
    if (!manualCode.trim()) return;
    onScan(manualCode.trim());
    setManualCode("");
    setManualEntryOpen(false);
  }

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      <ScanLine className="size-16 text-muted-foreground" />
      <h1 className="text-2xl font-semibold">
        {activeJob
          ? // The human job number, not `code` (the opaque Tharstern
            // working-operation id) — `jobNo` is always populated now (see
            // ActiveJob's docstring); `code` is a last-resort fallback only.
            `${activeJob.paused ? "Paused" : "Active"} — Tharstern job ${activeJob.jobNo ?? activeJob.code}`
          : "Ready — scan job bag"}
      </h1>

      {/* Site-handhelds-and-machine-visibility update — a glance here
          confirms what the live job is actually booked against; a
          mis-tap on the wrong guillotine is otherwise invisible after
          the fact. Nothing rendered when it can't be resolved (a
          login-reconstructed already-open job carries no machine
          detail) — never a placeholder. */}
      {activeJob?.machineName && (
        <p className="text-sm text-muted-foreground -mt-4">
          {activeJob.machineName}
        </p>
      )}

      {activeJob && (
        <div className="flex flex-col gap-3 w-full max-w-xs">
          {activeJob.paused ? (
            <Button size="lg" className="gap-2" onClick={onResumeJob}>
              <Play className="size-4" /> Resume job
            </Button>
          ) : (
            <Button
              variant="outline"
              size="lg"
              className="gap-2"
              onClick={onPauseJob}
            >
              <Pause className="size-4" /> Pause job
            </Button>
          )}
          <Button
            variant="outline"
            size="lg"
            className="gap-2"
            onClick={onEndJob}
          >
            <Square className="size-4" /> End job
          </Button>
        </div>
      )}

      <input
        ref={scanInputRef}
        value={scanBuffer}
        onChange={(e) => setScanBuffer(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && scanBuffer.trim()) {
            onScan(scanBuffer.trim());
            setScanBuffer("");
          }
        }}
        onBlur={() => {
          if (!manualEntryOpen) scanInputRef.current?.focus();
        }}
        className="sr-only"
        aria-label="Job-bag scan input"
      />

      {manualEntryOpen ? (
        <form onSubmit={submitManualCode} className="w-full max-w-xs space-y-3">
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Job-bag code"
            className="w-full rounded-md border border-border bg-card px-4 py-3 text-lg text-center outline-none focus:border-primary"
            aria-label="Manual job-bag code entry"
            autoFocus
          />
          <Button type="submit" size="lg" className="w-full">
            Submit
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setManualEntryOpen(false)}
          >
            Use scanner instead
          </Button>
        </form>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Button variant="outline" onClick={() => setManualEntryOpen(true)}>
            Enter code manually
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={onLookUpJob}
          >
            <Search className="size-4" /> Look up a Tharstern job
          </Button>
        </div>
      )}

      {feedback && (
        <FeedbackBanner feedback={feedback} onDismiss={onFeedbackDismissed} />
      )}
    </div>
  );
}

function FeedbackBanner({
  feedback,
  onDismiss,
}: {
  feedback: ScanFeedback;
  /** Only wired up for the refusal family below — success feedback clears
   * itself on FEEDBACK_DISMISS_MS and doesn't need an explicit control. */
  onDismiss: () => void;
}) {
  if (feedback.kind === "accepted") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-4 text-lg font-medium text-[color:var(--color-success,green)]">
        <CheckCircle2 className="size-6" /> Started Tharstern job{" "}
        {feedback.jobNo}
      </div>
    );
  }
  if (feedback.kind === "despatch_recorded") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-4 text-lg font-medium text-[color:var(--color-success,green)]">
        <PackageCheck className="size-6" /> Recorded:{" "}
        {despatchStepLabel(feedback.step)}
      </div>
    );
  }
  if (feedback.kind === "despatch_already_complete") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-4 text-lg font-medium text-muted-foreground">
        <PackageCheck className="size-6" /> This Tharstern job is already
        despatched.
      </div>
    );
  }
  if (feedback.kind === "took_over") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-4 text-lg font-medium text-[color:var(--color-success,green)]">
        <ArrowRightLeft className="size-6" /> Took over Tharstern job{" "}
        {feedback.jobNo}
      </div>
    );
  }
  if (feedback.kind === "job_finished") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-6 py-4 text-lg font-medium text-[color:var(--color-success,green)]">
        <CheckCircle2 className="size-6" />
        {feedback.finished
          ? `Job complete — logged ${feedback.quantityGood} good / ${feedback.quantityWaste} waste.`
          : `Logged ${feedback.quantityGood} good / ${feedback.quantityWaste} waste — left open for hand-over.`}
      </div>
    );
  }
  // rejected/partial_failure — the persisting refusal family (see
  // feedbackPersistsUntilDismissed). No auto-clear timer, so this is the
  // only feedback kind that needs its own explicit dismiss control.
  const isPartial = feedback.kind === "partial_failure";
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-6 py-4 text-lg font-medium ${
        isPartial
          ? "border-amber-500/40 text-amber-600"
          : "border-destructive/40 text-destructive"
      }`}
    >
      <XCircle className="size-6 shrink-0" />
      <span>{feedback.reason}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="ml-2 size-8 shrink-0 text-current hover:bg-current/10"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

// ------------------------------------------------------ Finish screen ---
// Job hand-over spec, Journey 1 — reached from "End job"/"Finish" on the
// Active/Paused screen. Replaces the old silent finish-at-planned-quantity
// behaviour: the finishing operator states their real good/waste quantity
// and an explicit finished/not-finished decision before anything is sent.

function FinishJobScreen({
  activeJob,
  onConfirm,
  onCancel,
}: {
  activeJob: ActiveJob;
  onConfirm: (
    finished: boolean,
    quantityGood: number,
    quantityWaste: number,
    comment: string,
  ) => void;
  onCancel: () => void;
}) {
  // Advisory (module update spec §10): default good quantity to the
  // operation's planned quantity so the common "ran the plan" case is one
  // confirm; the operator adjusts for variance. Falls back to 0 when the
  // planned quantity isn't known (a login-reconstructed job).
  const [quantityGood, setQuantityGood] = useState(
    activeJob.plannedQuantity ?? 0,
  );
  const [quantityWaste, setQuantityWaste] = useState(0);
  const [finished, setFinished] = useState<boolean | null>(null);
  const [comment, setComment] = useState("");

  // Second line of defence, kiosk-require-operator-comment fix — the real
  // enforcement is end_job's own validation (a blank comment on an
  // activity that requires one is a guaranteed Tharstern rejection); this
  // just stops the operator submitting toward it. `commentRequired`
  // undefined (a login-reconstructed job — see ActiveJob's docstring)
  // reads as "not required" here, never blocking on an unresolved hint.
  const commentRequired = activeJob.commentRequired ?? false;
  const commentMissing = commentRequired && comment.trim() === "";

  function submit(e: FormEvent) {
    e.preventDefault();
    if (finished === null || commentMissing) return;
    onConfirm(finished, quantityGood, quantityWaste, comment.trim());
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-6 w-full max-w-sm text-center"
    >
      <div>
        <h1 className="text-2xl font-semibold">
          Job {activeJob.jobNo ?? activeJob.code}
        </h1>
        <p className="text-muted-foreground">
          {activeJob.activityName ?? "Operation"}
        </p>
      </div>

      <QuantityField
        label="Good quantity"
        value={quantityGood}
        onChange={setQuantityGood}
      />
      <QuantityField
        label="Waste quantity"
        value={quantityWaste}
        onChange={setQuantityWaste}
      />

      <div className="space-y-2 text-left">
        <div className="text-sm font-medium text-muted-foreground text-center">
          Job finished?
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant={finished === true ? "default" : "outline"}
            size="lg"
            className="flex-1"
            aria-pressed={finished === true}
            onClick={() => setFinished(true)}
          >
            Yes, complete
          </Button>
          <Button
            type="button"
            variant={finished === false ? "default" : "outline"}
            size="lg"
            className="flex-1"
            aria-pressed={finished === false}
            onClick={() => setFinished(false)}
          >
            No — someone will continue
          </Button>
        </div>
      </div>

      <div className="space-y-1 text-left">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            commentRequired ? "Add a comment" : "Add a note (optional)"
          }
          className="w-full rounded-md border border-border bg-card px-4 py-3 text-base outline-none focus:border-primary"
          aria-label={
            commentRequired ? "Finish comment (required)" : "Finish comment"
          }
          aria-required={commentRequired}
          aria-invalid={commentMissing}
          aria-describedby={
            commentRequired ? "finish-job-comment-hint" : undefined
          }
        />
        {commentRequired && (
          <p
            id="finish-job-comment-hint"
            className="text-sm text-muted-foreground text-center"
          >
            A comment is required to finish this operation.
          </p>
        )}
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={finished === null || commentMissing}
      >
        Confirm finish
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

// ------------------------------------------- Choice quantity prompt ---
// fix/joboperations-field-mismatch, Journey 1 continuation — reached when
// the operator taps a new operation/bundle in the job_view picker while
// one is still open at this terminal. Same QuantityField input as
// FinishJobScreen above, deliberately smaller: this always finishes the
// prior operation as complete (finished=true, same-operator advance —
// see _scan_processing.finish_then_start), so there is no finished
// toggle or comment field here, only the two quantities. No pre-fill
// from a "planned quantity" — the operation being finished here (
// `previous_working_operation_id`, a *working-operation* id) can't be
// safely joined back to a job operation's planned figure client-side
// (a different id space — the same distinction the backend's own
// ShopfloorWorkingOperation docstring calls out), so this starts at 0/0
// like a genuinely blank entry, not a guess.
function ChoiceQuantityPromptScreen({
  scanEvent,
  onConfirm,
  onCancel,
}: {
  scanEvent: KioskScanEvent;
  onConfirm: (
    quantityGood: number,
    quantityWaste: number,
    comment: string,
  ) => void;
  onCancel: () => void;
}) {
  const [quantityGood, setQuantityGood] = useState(0);
  const [quantityWaste, setQuantityWaste] = useState(0);
  const [comment, setComment] = useState("");

  // Second line of defence, kiosk-require-operator-comment fix,
  // finish-then-start extension — same shape as FinishJobScreen's
  // commentRequired: a blank comment on an activity that requires one is
  // a guaranteed Tharstern rejection, so this stops the operator
  // submitting toward it. finish_then_start's own validation (via
  // choose_job_operation/choose_operation_bundle) is the real
  // enforcement; `previous_comment_required` undefined/unresolved reads
  // as "not required" here, never blocking on an unresolved hint.
  const commentRequired = scanEvent.previous_comment_required ?? false;
  const commentMissing = commentRequired && comment.trim() === "";

  function submit(e: FormEvent) {
    e.preventDefault();
    if (commentMissing) return;
    onConfirm(quantityGood, quantityWaste, comment.trim());
  }

  const jobNo = scanEvent.previous_working_operation_job_no;

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-6 w-full max-w-sm text-center"
    >
      <div>
        <h1 className="text-2xl font-semibold">
          Finish your current operation
        </h1>
        <p className="text-muted-foreground">
          {jobNo
            ? `Enter what you produced on Tharstern job ${jobNo} before starting the next one.`
            : "Enter what you produced before starting the next operation."}
        </p>
      </div>

      <QuantityField
        label="Good quantity"
        value={quantityGood}
        onChange={setQuantityGood}
      />
      <QuantityField
        label="Waste quantity"
        value={quantityWaste}
        onChange={setQuantityWaste}
      />

      <div className="space-y-1 text-left">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            commentRequired ? "Add a comment" : "Add a note (optional)"
          }
          className="w-full rounded-md border border-border bg-card px-4 py-3 text-base outline-none focus:border-primary"
          aria-label={
            commentRequired ? "Finish comment (required)" : "Finish comment"
          }
          aria-required={commentRequired}
          aria-invalid={commentMissing}
          aria-describedby={
            commentRequired ? "choice-quantity-prompt-comment-hint" : undefined
          }
        />
        {commentRequired && (
          <p
            id="choice-quantity-prompt-comment-hint"
            className="text-sm text-muted-foreground text-center"
          >
            A comment is required to finish this operation.
          </p>
        )}
      </div>

      <Button type="submit" size="lg" disabled={commentMissing}>
        Confirm and continue
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

/** Touch-first numeric entry with +/- stepper buttons alongside the raw
 * input — big tap targets, consistent with the rest of the kiosk's
 * touch-first controls, never negative. */
function QuantityField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  function step(delta: number) {
    onChange(Math.max(0, value + delta));
  }
  return (
    <div className="space-y-1 text-left">
      <label className="text-sm font-medium text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="px-4"
          onClick={() => step(-1)}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          <Minus className="size-4" />
        </Button>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="flex-1 rounded-md border border-border bg-card px-4 py-3 text-2xl font-semibold text-center outline-none focus:border-primary"
          aria-label={label}
        />
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="px-4"
          onClick={() => step(1)}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          <Plus className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Conflict ---

function ConflictTakeover({
  scanEvent,
  onResolve,
}: {
  scanEvent: KioskScanEvent;
  onResolve: (confirm: boolean) => void;
}) {
  const jobLabel = scanEvent.previous_working_operation_job_no
    ? `Job ${scanEvent.previous_working_operation_job_no}`
    : `Operation #${scanEvent.previous_working_operation_id}`;
  return (
    <div className="flex flex-col items-center gap-6 text-center max-w-lg">
      <TriangleAlert className="size-14 text-amber-500" />
      <h1 className="text-2xl font-semibold">
        You have another Tharstern job open
      </h1>
      <p className="text-muted-foreground">
        {jobLabel} is still open. Finish it and start this scan instead?
      </p>
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button size="lg" onClick={() => onResolve(true)}>
          Yes — finish it and start this one
        </Button>
        <Button variant="outline" size="lg" onClick={() => onResolve(false)}>
          Cancel — stay on that job
        </Button>
      </div>
    </div>
  );
}

// -------------------------------------------------- Take-over confirm ---
// Job hand-over spec, Journeys 2/3 — the "brief confirm" between tapping
// "Take over" on a held-by-other row and it becoming the operator's own
// active job. Deliberately mirrors ConflictTakeover's full-screen
// icon+headline+actions treatment: taking over is a decision, not a tap.

function TakeOverConfirm({
  operation,
  onConfirm,
  onCancel,
}: {
  operation: KioskJobOperation;
  onConfirm: (comment: string) => void;
  onCancel: () => void;
}) {
  const held = operation.held_by_other;
  const [comment, setComment] = useState("");

  // Second line of defence, kiosk-require-operator-comment fix — the
  // real enforcement is take_over_operation's own validation (a blank
  // comment on an activity that requires one is a guaranteed Tharstern
  // rejection); this just stops the operator submitting toward it.
  // Deliberately prompts the *taking-over* operator for their own note
  // rather than auto-generating one ("Taken over by X") — that would
  // write fabricated content into a timesheet record a human later reads
  // as the operator's own note.
  const commentRequired = operation.comment_required ?? false;
  const commentMissing = commentRequired && comment.trim() === "";

  return (
    <div className="flex flex-col items-center gap-6 text-center max-w-lg">
      <ArrowRightLeft className="size-14 text-amber-600" />
      <h1 className="text-2xl font-semibold">
        {held
          ? `Continue ${held.operator_name}'s work?`
          : "Continue this operator's work?"}
      </h1>
      <p className="text-muted-foreground">
        {operation.activity_name} ({operation.section_name}) —{" "}
        {held ? describeHeldByOther(held) : "held by another operator"}. Taking
        over starts your own booking for the rest of this operation.
      </p>

      <div className="w-full max-w-xs space-y-1 text-left">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            commentRequired ? "Add a comment" : "Add a note (optional)"
          }
          className="w-full rounded-md border border-border bg-card px-4 py-3 text-base outline-none focus:border-primary"
          aria-label={
            commentRequired
              ? "Take-over comment (required)"
              : "Take-over comment"
          }
          aria-required={commentRequired}
          aria-invalid={commentMissing}
          aria-describedby={
            commentRequired ? "take-over-comment-hint" : undefined
          }
        />
        {commentRequired && (
          <p
            id="take-over-comment-hint"
            className="text-sm text-muted-foreground text-center"
          >
            A comment is required to take over this operation.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button
          size="lg"
          disabled={commentMissing}
          onClick={() => onConfirm(comment.trim())}
        >
          Yes — take over
        </Button>
        <Button variant="outline" size="lg" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------- Job view ---
// The unified production + despatch timeline (despatch-tracking spec §5,
// Addendum A1) — one component for both the scan-driven choice phase and
// the read-only job-lookup phase, so "scan and type-in render the SAME job
// view component" (Addendum A1) is structural, not just a shared look.

const MILESTONE_ICON: Record<
  ReturnType<typeof milestoneGroup>,
  { icon: typeof CheckCircle2; className: string }
> = {
  done: {
    icon: CheckCircle2,
    className: "text-[color:var(--color-success,green)]",
  },
  current: { icon: CircleDot, className: "text-primary" },
  upcoming: { icon: Circle, className: "text-muted-foreground" },
};

/** Human label for a non-default, not-done picker row (the "Step N" slot
 * used to hold a Tharstern ordinal that doesn't exist — see
 * `JobTimelinePanel`'s own docstring). Leads with genuinely populated
 * data — the operation's own status — rather than a position-derived
 * number that would read as a stable plan step it isn't. */
const MILESTONE_STATUS_LABEL: Record<KioskJobOperationStatus, string> = {
  unplanned: "Unplanned",
  lifted: "Lifted",
  planned: "Planned",
  started: "Started",
  stopped: "Stopped",
  finished: "Finished",
  external: "External",
  purged: "Purged",
  unknown: "Status unknown",
};

function formatMilestoneTime(iso: string | null): string | null {
  if (!iso) return null;
  return formatTimeOrDash(iso);
}

type JobTimelineMode = "confirm-operation" | "confirm-despatch" | "read-only";

/** Tharstern's scheduling and shop-floor-data-collection subsystems run
 * independently, so operators routinely and legitimately start operations
 * out of the planned order — this must show every not-done operation the
 * job has, never a filtered subset, only bias visually toward the
 * server-suggested one (mirrors
 * docs/features/shopfloor-barcode-scanning/
 * 09_module_update_spec_operation_choice.md, extended to despatch steps by
 * the despatch-tracking spec's Addendum A1).
 *
 * `data.milestones` is rendered in the order the API returned it — never
 * re-sorted client-side. `/api/joboperations` carries no sequence/step
 * number (kiosk-owned-activity-mapping, 2026-08-27), so there is nothing
 * honest to sort by; the endpoint's own response order (its stored
 * procedure's `ORDER BY` — paper line, section, planned start) is the
 * only real ordering signal available, and it's already what a re-sort
 * would have produced when the sort key was a constant.
 *
 * `mode` governs which section (if either) is tappable:
 * - "confirm-operation": production's not-done rows call `onChooseOperation`.
 * - "confirm-despatch": despatch's outstanding rows call `onChooseDespatchStep`.
 * - "read-only": nothing here is tappable — `onStartNext` (job-lookup's
 *   "Start next step") submits a fresh scan and lets the server decide,
 *   exactly as a real scan would (a job-bag barcode only ever identifies
 *   the job, never a specific operation or despatch step).
 *
 * The despatch section only renders once it has something to show —
 * production done or despatch steps already recorded — so a job still
 * mid-production doesn't show a noisy "all five outstanding" despatch list
 * that isn't reachable yet. */
function JobTimelinePanel({
  data,
  mode,
  bundleChoices,
  onChooseOperation,
  onChooseBundle,
  onChooseDespatchStep,
  onStartNext,
  onOffPlan,
  onTakeOver,
}: {
  data: KioskJobOperationsLookup;
  mode: JobTimelineMode;
  /** Operation-bundle candidates (shopfloor-operation-bundles update spec
   * §9/§11) — additive to the milestones below, never a replacement, and
   * only ever tappable in "confirm-operation" mode. */
  bundleChoices?: KioskBundleChoice[];
  onChooseOperation?: (operationId: number) => void;
  onChooseBundle?: (idcResourceSequenceId: number) => void;
  onChooseDespatchStep?: (step: KioskDespatchStep) => void;
  onStartNext?: () => void;
  onOffPlan: () => void;
  /** Job hand-over spec — present regardless of `mode`: an operation held
   * by another operator renders distinctly and takes priority over the
   * mode-specific tappable/plain treatment below. */
  onTakeOver?: (operation: KioskJobOperation) => void;
}) {
  const milestones = data.milestones;
  const despatchDone = data.despatch_steps_done ?? [];
  const despatchOutstanding = data.despatch_steps_outstanding ?? [];
  const productionComplete = milestones.every(
    (m) => milestoneGroup(m.status) === "done",
  );
  const showDespatch = productionComplete || despatchDone.length > 0;
  const hasNext =
    data.suggested_next_operation_id != null ||
    data.suggested_next_despatch_step != null;
  const doneSteps = new Set(despatchDone.map((s) => s.step));

  return (
    <div className="flex flex-col gap-6 text-left w-full max-w-lg">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">Tharstern job {data.job_no}</h1>
      </div>

      {mode === "confirm-operation" &&
        bundleChoices != null &&
        bundleChoices.length > 0 && (
          // Operation bundles (shopfloor-operation-bundles product brief §11 /
          // update spec §11, kiosk choice UI — approved Option B): a bundle
          // sits at the top of the choice list, visually distinguished (accent
          // border + "bundle · N steps" badge) with its member steps as
          // chips, so it reads as "several steps, one tap". Additive — the
          // single-op choices below are never hidden by it.
          <ol className="space-y-2">
            {bundleChoices.map((bundle) => (
              <li key={bundle.idc_resource_sequence_id}>
                <button
                  type="button"
                  onClick={() =>
                    onChooseBundle?.(bundle.idc_resource_sequence_id)
                  }
                  aria-label={
                    // Real machine names disambiguate Tharstern's near-
                    // identical activities the same way they do for the
                    // single-op choice below (site-handhelds-and-machine-
                    // visibility update) — identifies the bundle the way an
                    // operator would say it, not just its plan name.
                    bundle.machine_name
                      ? `${bundle.name} — ${bundle.machine_name}`
                      : bundle.name
                  }
                  className="w-full flex flex-col gap-2 rounded-lg border-2 border-primary bg-primary/5 px-4 py-3 text-left transition-colors hover:border-primary"
                >
                  <div className="flex items-center gap-3">
                    <Layers className="size-5 shrink-0 text-primary" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{bundle.name}</div>
                      {bundle.machine_name && (
                        <div className="text-sm text-muted-foreground">
                          {bundle.machine_name}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-primary">
                      bundle · {bundle.members.length} steps
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pl-8">
                    {bundle.members
                      .slice()
                      .sort((a, b) => a.sequence_no - b.sequence_no)
                      .map((member) => (
                        <span
                          key={member.sequence_no}
                          className="rounded-full border border-border bg-card px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {member.description}
                        </span>
                      ))}
                  </div>
                  <div className="pl-8 text-sm text-muted-foreground">
                    Expected next
                  </div>
                </button>
              </li>
            ))}
          </ol>
        )}

      <ol className="space-y-2">
        {milestones.map((m) => {
          const group = milestoneGroup(m.status);
          const { icon: Icon, className } = MILESTONE_ICON[group];
          const start = formatMilestoneTime(m.actual_start ?? m.planned_start);
          const end = formatMilestoneTime(m.actual_end);
          const isDefault = m.operation_id === data.suggested_next_operation_id;
          const tappable = mode === "confirm-operation" && group !== "done";

          // Job hand-over spec — an operation in progress / left
          // incomplete by a *different* operator reads as "continue their
          // work", never as a normal Expected-next candidate, regardless
          // of `mode` (scan-driven confirm-operation or read-only lookup).
          if (group !== "done" && m.held_by_other) {
            const held = m.held_by_other;
            return (
              <li key={m.operation_id}>
                <div className="flex items-center gap-3 rounded-lg border-2 border-dashed border-amber-500/50 bg-amber-500/5 px-4 py-3">
                  <ArrowRightLeft className="size-5 shrink-0 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{m.activity_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {m.section_name}
                    </div>
                    <div className="text-sm text-amber-700 mt-0.5">
                      {describeHeldByOther(held)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0 border-amber-500/60 gap-1.5"
                    onClick={() => onTakeOver?.(m)}
                  >
                    <ArrowRightLeft className="size-3.5" /> Take over
                  </Button>
                </div>
              </li>
            );
          }

          if (tappable) {
            return (
              <li key={m.operation_id}>
                <button
                  type="button"
                  onClick={() => onChooseOperation?.(m.operation_id)}
                  aria-label={
                    // Real machine names disambiguate Tharstern's near-
                    // identical activities (site-handhelds-and-machine-
                    // visibility update — e.g. six guillotines all named
                    // "Guillotine") — identifies the operation the way an
                    // operator would say it, not just its activity name.
                    m.machine_name
                      ? `${m.activity_name} — ${m.machine_name}`
                      : m.activity_name
                  }
                  className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors hover:border-primary ${
                    isDefault
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card/40"
                  }`}
                >
                  {isDefault ? (
                    <CircleDot className="size-5 shrink-0 text-primary" />
                  ) : (
                    <Circle className="size-5 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{m.activity_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {m.section_name}
                      {m.machine_name && ` · ${m.machine_name}`}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground shrink-0">
                    {isDefault
                      ? "Expected next"
                      : MILESTONE_STATUS_LABEL[m.status]}
                  </div>
                </button>
              </li>
            );
          }
          return (
            <li
              key={m.operation_id}
              className="flex items-center gap-3 rounded-lg border border-border bg-card/40 px-4 py-3"
            >
              <Icon className={`size-5 shrink-0 ${className}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{m.activity_name}</div>
                <div className="text-sm text-muted-foreground">
                  {m.section_name}
                </div>
              </div>
              <div className="text-sm text-muted-foreground text-right shrink-0">
                {group === "done" && end && `Finished ${end}`}
                {group === "current" && start && `Started ${start}`}
                {group === "upcoming" && "Upcoming"}
              </div>
            </li>
          );
        })}
      </ol>

      {showDespatch && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground px-1">
            Despatch
          </div>
          <ol className="space-y-2">
            {despatchDone.map((s) => (
              <li
                key={s.step}
                className="flex items-center gap-3 rounded-lg border border-border bg-card/40 px-4 py-3"
              >
                <CheckCircle2 className="size-5 shrink-0 text-[color:var(--color-success,green)]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{despatchStepLabel(s.step)}</div>
                  <div className="text-sm text-muted-foreground">
                    {s.operator_display_name}
                  </div>
                </div>
                {s.finished_at == null && (
                  <div className="text-sm text-muted-foreground shrink-0">
                    Current
                  </div>
                )}
              </li>
            ))}
            {despatchOutstanding
              .filter((step) => !doneSteps.has(step))
              .map((step) => {
                const isDefault = step === data.suggested_next_despatch_step;
                const tappable = mode === "confirm-despatch";
                if (tappable) {
                  return (
                    <li key={step}>
                      <button
                        type="button"
                        onClick={() => onChooseDespatchStep?.(step)}
                        className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors hover:border-primary ${
                          isDefault
                            ? "border-primary bg-primary/5"
                            : "border-border bg-card/40"
                        }`}
                      >
                        {isDefault ? (
                          <CircleDot className="size-5 shrink-0 text-primary" />
                        ) : (
                          <Circle className="size-5 shrink-0 text-muted-foreground" />
                        )}
                        <div className="flex-1 min-w-0 font-medium">
                          {despatchStepLabel(step)}
                        </div>
                        <div className="text-sm text-muted-foreground shrink-0">
                          {isDefault ? "Expected next" : "Outstanding"}
                        </div>
                      </button>
                    </li>
                  );
                }
                return (
                  <li
                    key={step}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card/40 px-4 py-3"
                  >
                    <Circle className="size-5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      {despatchStepLabel(step)}
                    </div>
                    <div className="text-sm text-muted-foreground shrink-0">
                      Outstanding
                    </div>
                  </li>
                );
              })}
          </ol>
        </div>
      )}

      {mode === "read-only" && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card/40 p-4">
          {hasNext ? (
            <>
              {/* Starting from here is indistinguishable, to the backend,
                  from starting by scan: a job-bag barcode only ever
                  identifies the job, never a specific operation or despatch
                  step, so this submits the same job_no a scan would and
                  lets the same server-side resolution decide what's next. */}
              <Button size="lg" onClick={onStartNext}>
                Start next step
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                or scan the job bag
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              Nothing outstanding on this Tharstern job.
            </p>
          )}
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        className="gap-2 self-center"
        onClick={onOffPlan}
      >
        <Wrench className="size-4" /> Off-plan / different machine
      </Button>
    </div>
  );
}

/** The scan-driven job_view phase — `operation_choice_pending` or
 * `despatch_choice_pending`. Guards defensively against a missing
 * `job_view` (should never happen per the backend contract, but a schema
 * mismatch must not crash the terminal). */
function JobViewScanPanel({
  scanEvent,
  onChooseOperation,
  onChooseBundle,
  onChooseDespatchStep,
  onOffPlan,
  onTakeOver,
}: {
  scanEvent: KioskScanEvent;
  onChooseOperation: (operationId: number) => void;
  onChooseBundle: (idcResourceSequenceId: number) => void;
  onChooseDespatchStep: (step: KioskDespatchStep) => void;
  onOffPlan: (jobNo: string) => void;
  onTakeOver: (jobNo: string, operation: KioskJobOperation) => void;
}) {
  if (!scanEvent.job_view) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <TriangleAlert className="size-12 text-amber-500" />
        <p className="text-muted-foreground">
          Couldn't load this Tharstern job's details — scan again.
        </p>
      </div>
    );
  }
  const mode: JobTimelineMode =
    scanEvent.outcome === "despatch_choice_pending"
      ? "confirm-despatch"
      : "confirm-operation";
  const jobView = scanEvent.job_view;
  // Bundle entries (shopfloor-operation-bundles update spec §9) live on
  // the flat `operation_choices` union, not on `job_view` — that response
  // field stays the pure production/despatch timeline. Additive to the
  // single-op candidates job_view already carries.
  const bundleChoices: KioskBundleChoice[] = (
    scanEvent.operation_choices ?? []
  ).filter((choice): choice is KioskBundleChoice => choice.kind === "bundle");
  return (
    <JobTimelinePanel
      data={jobView}
      mode={mode}
      bundleChoices={bundleChoices}
      onChooseOperation={onChooseOperation}
      onChooseBundle={onChooseBundle}
      onChooseDespatchStep={onChooseDespatchStep}
      onOffPlan={() => onOffPlan(jobView.job_no)}
      onTakeOver={(operation) => onTakeOver(jobView.job_no, operation)}
    />
  );
}

// -------------------------------------------------------- Job lookup ---

/** Separate, opt-in "what's happened / what's next" view for a job bag —
 * see the file header note. Never runs as a side effect of a normal
 * job-bag scan; the golden path in ReadyPanel is untouched. Renders the
 * same `JobTimelinePanel` a scan's job_view phase does (Addendum A1). */
function JobLookupPanel({
  lookup,
  onLookup,
  onStart,
  onOffPlan,
  onTakeOver,
  onBack,
}: {
  lookup: JobLookupStatus;
  onLookup: (jobNo: string) => void;
  onStart: (scannedCode: string) => void;
  onOffPlan: (jobNo: string) => void;
  onTakeOver: (jobNo: string, operation: KioskJobOperation) => void;
  onBack: () => void;
}) {
  const [jobNoInput, setJobNoInput] = useState("");

  function submitLookup(e: FormEvent) {
    e.preventDefault();
    if (!jobNoInput.trim()) return;
    onLookup(jobNoInput.trim());
  }

  if (lookup.kind === "entering") {
    return (
      <div className="flex flex-col items-center gap-6 text-center w-full max-w-xs">
        <Search className="size-14 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">Look up a Tharstern job</h1>
        <p className="text-muted-foreground">
          Scan the job number, or type it below.
        </p>
        <form onSubmit={submitLookup} className="w-full space-y-3">
          <input
            value={jobNoInput}
            onChange={(e) => setJobNoInput(e.target.value)}
            placeholder="Job number"
            inputMode="numeric"
            className="w-full rounded-md border border-border bg-card px-4 py-3 text-lg text-center outline-none focus:border-primary"
            aria-label="Job number"
            autoFocus
          />
          <Button type="submit" size="lg" className="w-full">
            Look up
          </Button>
        </form>
        <Button variant="ghost" size="sm" className="gap-2" onClick={onBack}>
          <ArrowLeft className="size-4" /> Back to scanning
        </Button>
      </div>
    );
  }

  if (lookup.kind === "loading") {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <Loader2 className="size-10 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">Looking up job…</p>
      </div>
    );
  }

  if (lookup.kind === "not_found" || lookup.kind === "error") {
    return (
      <div className="flex flex-col items-center gap-6 text-center w-full max-w-xs">
        <TriangleAlert className="size-12 text-amber-500" />
        <h1 className="text-xl font-semibold">
          {lookup.kind === "not_found"
            ? `Tharstern job ${lookup.jobNo} not found`
            : "Couldn't reach the server"}
        </h1>
        <div className="flex flex-col gap-3 w-full">
          <Button variant="outline" onClick={() => onLookup(lookup.jobNo)}>
            Try again
          </Button>
          <Button variant="ghost" size="sm" className="gap-2" onClick={onBack}>
            <ArrowLeft className="size-4" /> Back to scanning
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 items-center">
      {lookup.note && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-[color:var(--color-success,green)]">
          <CheckCircle2 className="size-4" /> {lookup.note}
        </div>
      )}
      <JobTimelinePanel
        data={lookup.data}
        mode="read-only"
        onStartNext={() => onStart(lookup.data.job_no)}
        onOffPlan={() => onOffPlan(lookup.data.job_no)}
        onTakeOver={(operation) => onTakeOver(lookup.data.job_no, operation)}
      />
      <Button variant="ghost" size="sm" className="gap-2" onClick={onBack}>
        <ArrowLeft className="size-4" /> Back to scanning
      </Button>
    </div>
  );
}

// ----------------------------------------------------------- Off-plan ---
// The activity picker feed (Addendum A2). Search AND department filter are
// both server-side (GET /shopfloor-kiosk/activities?department_id=&q=...)
// — search is debounced lightly so a fast typist doesn't fire a request
// per keystroke; the picker never filters the returned list client-side.
//
// Department options aren't a separate endpoint — there are hundreds of
// activities but only a handful of departments, so `departmentOptions`
// accumulates the distinct (department_id, department_name) pairs seen
// across every fetch this picker session has made (see the `useEffect`
// below). Accumulating rather than replacing means picking a department
// doesn't shrink the dropdown down to just that one department once its
// filtered response comes back.

function collectDepartmentOptions(
  existing: readonly { id: number; name: string }[],
  activities: readonly KioskShopfloorActivity[],
): { id: number; name: string }[] {
  const byId = new Map(existing.map((d) => [d.id, d.name]));
  for (const activity of activities) {
    if (!byId.has(activity.department_id)) {
      byId.set(activity.department_id, activity.department_name);
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

const ALL_DEPARTMENTS_VALUE = "all";

function OffPlanPicker({
  status,
  onSearch,
  onStart,
  onCancel,
}: {
  status: OffPlanStatus;
  onSearch: (query: string, departmentId: number | undefined) => void;
  onStart: (
    activity: KioskShopfloorActivity,
    currentActivities: KioskShopfloorActivity[],
  ) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined,
  );
  const [departmentOptions, setDepartmentOptions] = useState<
    { id: number; name: string }[]
  >([]);

  useEffect(() => {
    const id = setTimeout(() => onSearch(query, departmentId), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSearch is a stable per-render closure over phase state; re-running on query/departmentId change is what matters.
  }, [query, departmentId]);

  const activities =
    status.kind === "ready"
      ? status.activities
      : status.kind === "starting"
        ? [status.activity]
        : status.kind === "start_error"
          ? status.activities
          : [];

  // Only "ready"/"start_error" carry a genuine fetched list (a stable
  // array reference held in Phase state) — using that as the effect's
  // dependency, rather than the `activities` literal above (which is a
  // fresh array on every render for "starting"), avoids re-running on
  // every render and looping via setDepartmentOptions.
  const fetchedActivities =
    status.kind === "ready" || status.kind === "start_error"
      ? status.activities
      : null;

  useEffect(() => {
    if (!fetchedActivities || fetchedActivities.length === 0) return;
    setDepartmentOptions((prev) =>
      collectDepartmentOptions(prev, fetchedActivities),
    );
  }, [fetchedActivities]);

  return (
    <div className="flex flex-col gap-6 text-left w-full max-w-lg">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">Off-plan operation</h1>
        <p className="text-muted-foreground">
          Not on this Tharstern job's route — a missed step, or a different
          machine.
        </p>
      </div>

      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search machines / activities"
          className="flex-1 min-w-0 rounded-md border border-border bg-card px-4 py-3 text-lg outline-none focus:border-primary"
          aria-label="Search shopfloor activities"
          autoFocus
        />
        <Select
          value={
            departmentId != null ? String(departmentId) : ALL_DEPARTMENTS_VALUE
          }
          onValueChange={(value) =>
            setDepartmentId(
              value === ALL_DEPARTMENTS_VALUE ? undefined : Number(value),
            )
          }
        >
          <SelectTrigger
            className="w-[160px] shrink-0 h-auto py-3"
            aria-label="Filter by department"
          >
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPARTMENTS_VALUE}>
              All departments
            </SelectItem>
            {departmentOptions.map((department) => (
              <SelectItem key={department.id} value={String(department.id)}>
                {department.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {status.kind === "start_error" && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/40 px-4 py-3 text-sm font-medium text-destructive">
          <XCircle className="size-5 shrink-0" /> {status.message}
        </div>
      )}

      {status.kind === "loading" && (
        <div className="flex flex-col items-center gap-3 py-6">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {status.kind === "error" && (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <TriangleAlert className="size-10 text-amber-500" />
          <p className="text-muted-foreground">Couldn't load activities.</p>
          <Button
            variant="outline"
            onClick={() => onSearch(query, departmentId)}
          >
            Try again
          </Button>
        </div>
      )}

      {activities.length > 0 && (
        <ol className="space-y-2">
          {activities.map((activity) => {
            const isStarting =
              status.kind === "starting" && status.activity.id === activity.id;
            return (
              <li key={activity.id}>
                <button
                  type="button"
                  disabled={status.kind === "starting"}
                  onClick={() => onStart(activity, activities)}
                  className="w-full flex items-center gap-3 rounded-lg border border-border bg-card/40 px-4 py-3 text-left transition-colors hover:border-primary disabled:opacity-60"
                >
                  {isStarting ? (
                    <Loader2 className="size-5 shrink-0 animate-spin text-primary" />
                  ) : (
                    <Wrench className="size-5 shrink-0 text-muted-foreground" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{activity.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {activity.department_name} · {activity.category}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {status.kind === "ready" && activities.length === 0 && (
        <p className="text-sm text-muted-foreground text-center">
          {query
            ? `No activities match "${query}".`
            : "No activities match this department."}
        </p>
      )}

      <Button
        variant="ghost"
        size="sm"
        className="gap-2 self-center"
        onClick={onCancel}
      >
        <ArrowLeft className="size-4" /> Cancel
      </Button>
    </div>
  );
}

// --------------------------------------------------- Terminal setup ---

function TerminalSetupScreen({
  onSetUp,
}: {
  onSetUp: (token: string) => void;
}) {
  const [token, setToken] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    onSetUp(token.trim());
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-muted p-8">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6"
      >
        <div className="flex flex-col items-center gap-2 text-center mb-2">
          <Settings className="size-8 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Set up this terminal</h1>
          <p className="text-sm text-muted-foreground">
            One-time step for whoever installs this kiosk. Paste the token from
            provisioning it in the admin settings — its name and site are read
            from the terminal automatically.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Terminal token</label>
          <input
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Paste plaintext token"
            className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary"
            autoFocus
          />
        </div>
        <Button type="submit" size="lg" className="w-full">
          Save and continue
        </Button>
      </form>
    </div>
  );
}
